import { useCallback, useEffect, useState } from 'react'

import { DEFAULT_LANGUAGE, Language } from '../../constants'
import { useAppContext } from '../../hooks/useAppContext'
import { VisitCreated } from '../../services/event-bus/events'
import { CommandsTable } from '../Dictation/components/CommandsTable'
import { useDeveloperContext } from '../Settings/Developer/hooks/useDeveloperContext'
import { ShowSidebarButton } from '../UI/AppBar/ShowSidebarButton'
import { CreateVisit } from '../UI/CreateVisit'
import { VisitDictationNavbar } from '../UI/VisitDictationNavbar'
import { ConfigNewVisit } from '../Visit/components/ConfigVisit'
import { MicrophoneSettings } from './Components/MicrophoneSettings'
import { StartRecordButton } from './Components/StartRecordButton'

interface Props {
    type: 'visit' | 'dictation'
}

export const WelcomeState = ({ type }: Props) => {
    const { preferences } = useAppContext().preferences
    const { isWhisperTranscriber, setAzureTranscriber, setWhisperTranscriber } = useDeveloperContext().developer
    const { templateId, customTemplateId, isTranslationEnabled, languageSource, languageTarget, setTemplateId, setlanguageTarget, setlanguageSource, setIsTranslationEnabled } =
        useForm(isWhisperTranscriber, preferences?.defaultTemplateId, preferences?.defaultCustomTemplateId, preferences?.defaultNoteLanguage)

    const onSetLanguageTarget = useCallback(
        (language: Language) => {
            setlanguageTarget(language)

            // If Whisper transcriber is enabled with non-English language, then switch to Azure STT
            if (isWhisperTranscriber && language !== 'en-US') {
                setAzureTranscriber()
            }
        },
        [isWhisperTranscriber, setAzureTranscriber, setlanguageTarget]
    )

    return (
        <div className="bg-white flex flex-col items-center justify-center h-full">
            <ShowSidebarButton />
            <VisitDictationNavbar />
            <div className="flex-1 h-full w-full flex flex-col items-center justify-center text-center">
                <CreateVisit
                    templateId={templateId}
                    customTemplateId={customTemplateId}
                    languageSource={isTranslationEnabled ? languageSource : undefined}
                    languageTarget={languageTarget}
                    childNode={(isLoading) => <StartRecordButton isLoading={isLoading} />}
                    onCreated={() => VisitCreated.emit()}
                    startRecordingOnLoad
                    type={type}
                />
                <div className="mt-4 mb-6 text-lg font-semibold text-gray-800">Start new {type === 'visit' ? 'Recording' : 'Dictation'}</div>
                <MicrophoneSettings />
                {type === 'visit' ? (
                    <div className="w-full max-w-md p-3">
                        <ConfigNewVisit
                            setAzureTranscriber={setAzureTranscriber}
                            setWhisperTranscriber={setWhisperTranscriber}
                            onSetLanguageTarget={onSetLanguageTarget}
                            isTranslationEnabled={isTranslationEnabled}
                            languageSource={languageSource}
                            languageTarget={languageTarget}
                            setTemplateId={setTemplateId}
                            setlanguageSource={setlanguageSource}
                            setlanguageTarget={setlanguageTarget}
                            customTemplateId={customTemplateId}
                            templateId={templateId}
                            isWhisperTranscriber={isWhisperTranscriber}
                            setIsTranslationEnabled={setIsTranslationEnabled}
                        />
                    </div>
                ) : (
                    <CommandsTable />
                )}
            </div>
        </div>
    )
}

const useForm = (_isWhisperTranscriber: boolean, defaultTemplateId?: string, defaultCustomTemplateId?: string, defaultLanguage?: Language) => {
    const [templateId, setTemplateId] = useState<string | undefined>(defaultTemplateId ?? undefined)
    const [customTemplateId, setCustomTemplateId] = useState<string | undefined>(defaultTemplateId ?? undefined)

    const [isTranslationEnabled, setIsTranslationEnabled] = useState(false)
    const [languageSource, setlanguageSource] = useState<Language | undefined>(undefined)
    const [languageTarget, setlanguageTarget] = useState<Language>(defaultLanguage ?? DEFAULT_LANGUAGE)

    useEffect(() => setTemplateId(defaultTemplateId ?? undefined), [defaultTemplateId])
    useEffect(() => setCustomTemplateId(defaultCustomTemplateId ?? undefined), [defaultCustomTemplateId])
    useEffect(() => setlanguageTarget(defaultLanguage ?? DEFAULT_LANGUAGE), [defaultLanguage])

    const setTemplateIdHander = useCallback((_templateId: string, isCustomTemplate: boolean) => {
        setTemplateId(!isCustomTemplate ? _templateId : undefined)
        setCustomTemplateId(isCustomTemplate ? _templateId : undefined)
    }, [])

    return {
        templateId,
        customTemplateId,
        setTemplateId: setTemplateIdHander,
        isTranslationEnabled,
        setIsTranslationEnabled,
        languageSource,
        setlanguageSource,
        languageTarget,
        setlanguageTarget
    }
}
